import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'utils/i18n';
import { useDarkMode } from 'utils/darkMode';
import sharedTranslations from '../shared/translations';
import { sections, typography, colors, spacing, interactive } from './legalStyles';

// Minimal markdown rendering (headings, lists, paragraphs, mermaid diagrams)
function renderMarkdown(md, containerId = '', isDarkMode = false) {
  if (!md) return null;
  // Very light conversion: split by lines and infer bullets/paragraphs
  const lines = String(md).split('\n');
  const elements = [];
  let list = null;
  let mermaidBlock = null;
  let mermaidIndex = 0;

  // Dark mode-aware text color
  const textColor = isDarkMode ? '#cbd5e1' : colors.text;
  const headingColor = isDarkMode ? '#e5e7eb' : colors.text;

  const flushList = () => {
    if (list) {
      elements.push(
        <ul 
          key={`ul-${elements.length}`} 
          style={{ 
            marginTop: spacing.xs, 
            marginBottom: spacing.sm, 
            paddingLeft: spacing.lg,
            ...typography.body,
            color: textColor
          }}
        >
          {list}
        </ul>
      );
      list = null;
    }
  };

  const flushMermaid = () => {
    if (mermaidBlock) {
      const mermaidId = `mermaid-${containerId}-${mermaidIndex++}`;
      const mermaidCode = mermaidBlock.join('\n');
      elements.push(
        <div 
          key={mermaidId}
          className="mermaid" 
          data-mermaid={mermaidCode}
          style={{ 
            marginTop: spacing.md, 
            marginBottom: spacing.md,
            textAlign: 'center'
          }}
        >
          {mermaidCode}
        </div>
      );
      mermaidBlock = null;
    }
  };

  lines.forEach((line, idx) => {
    const l = line.trim();
    
    // Check for mermaid code block
    if (l.startsWith('```mermaid')) {
      flushList();
      flushMermaid();
      mermaidBlock = [];
      return;
    }
    if (l === '```' && mermaidBlock !== null) {
      flushList();
      flushMermaid();
      return;
    }
    if (mermaidBlock !== null) {
      mermaidBlock.push(l);
      return;
    }
    
    if (!l) {
      flushList();
      return;
    }
    if (l.startsWith('- ') || l.startsWith('* ')) {
      if (!list) list = [];
      list.push(<li key={`li-${idx}`} style={{ ...typography.body, color: textColor }}>{l.slice(2)}</li>);
      return;
    }
    if (/^#{1,6}\s+/.test(l)) {
      flushList();
      const level = (l.match(/^#+/)[0] || '').length;
      const text = l.replace(/^#{1,6}\s+/, '');
      const Tag = `h${Math.min(level + 1, 6)}`;
      const headingStyle = level === 1 ? typography.h2 : typography.h3;
      elements.push(
        <Tag key={`h-${idx}`} style={{ ...headingStyle, marginTop: spacing.sm, marginBottom: spacing.xs, color: headingColor }}>
          {text}
        </Tag>
      );
      return;
    }
    // Bold text (markdown **text**)
    if (l.includes('**')) {
      flushList();
      const parts = l.split(/(\*\*[^*]+\*\*)/g);
      const formatted = parts.map((part, pIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={pIdx}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      elements.push(
        <p key={`p-${idx}`} style={{ ...typography.body, marginTop: spacing.xs, marginBottom: spacing.sm, color: textColor }}>
          {formatted}
        </p>
      );
      return;
    }
    // paragraph
    flushList();
    elements.push(
      <p key={`p-${idx}`} style={{ ...typography.body, marginTop: spacing.xs, marginBottom: spacing.sm, color: textColor }}>
        {l}
      </p>
    );
  });

  flushList();
  flushMermaid();
  return elements;
}

/**
 * LegalSection
 * Renders a section with anchor id and optional children.
 * Uses standardized styling for consistency.
 * Props:
 * - section: { id, title, content, children? }
 * - level: starting heading level (2 for h2)
 */
const LegalSection = ({ section, level = 2 }) => {
  const { language } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const [open, setOpen] = useState(true);
  const sectionRef = useRef(null);
  const HeadingTag = `h${Math.min(level, 6)}`;
  
  // Get translations - memoized to update when language changes
  const translations = useMemo(() => {
    const t = sharedTranslations[language] || sharedTranslations.en;
    return {
      collapseTitle: t?.legal?.section?.collapseTitle || 'Collapse',
      expandTitle: t?.legal?.section?.expandTitle || 'Expand',
      hide: t?.legal?.section?.hide || 'Hide',
      show: t?.legal?.section?.show || 'Show',
      copyLinkTitle: t?.legal?.section?.copyLinkTitle || 'Copy link'
    };
  }, [language]);

  // Initialize mermaid when section is open and component mounts
  useEffect(() => {
    if (open && sectionRef.current) {
      // Check if mermaid elements exist
      const mermaidElements = sectionRef.current.querySelectorAll('.mermaid[data-mermaid]');
      if (mermaidElements.length === 0) return;
      
      // Format mermaid code as plain text (fallback when mermaid is not available)
      const formatMermaidAsText = () => {
        mermaidElements.forEach((el) => {
          const mermaidCode = el.getAttribute('data-mermaid');
          el.style.padding = spacing.md;
          el.style.backgroundColor = isDarkMode ? '#2d3748' : colors.backgroundHover;
          el.style.border = `1px solid ${isDarkMode ? '#3a4553' : colors.border}`;
          el.style.borderRadius = '4px';
          el.style.fontFamily = 'monospace';
          el.style.fontSize = '12px';
          el.style.whiteSpace = 'pre-wrap';
          el.style.color = isDarkMode ? '#cbd5e1' : colors.text;
          el.textContent = mermaidCode;
        });
      };
      
      // Try to load mermaid - only use window.mermaid if available (CDN)
      // If mermaid is not available, show code as formatted text
      if (typeof window !== 'undefined' && window.mermaid) {
        try {
          const mermaid = window.mermaid;
          mermaid.initialize({ 
            startOnLoad: false,
            theme: 'default',
            securityLevel: 'loose'
          });
          
          mermaidElements.forEach((el, idx) => {
            if (el.querySelector('svg')) return;
            const mermaidId = `mermaid-${section.id}-${idx}-${Date.now()}`;
            const mermaidCode = el.getAttribute('data-mermaid');
            el.id = mermaidId;
            el.textContent = '';
            mermaid.render(mermaidId, mermaidCode).then((result) => {
              el.innerHTML = result.svg;
            }).catch(() => {
              el.textContent = mermaidCode;
            });
          });
        } catch (err) {
          formatMermaidAsText();
        }
      } else {
        // Mermaid not available - show code as plain text
        formatMermaidAsText();
      }
    }
  }, [open, section.id]);

  const sectionStyle = {
    marginBottom: sections.marginBottom,
    padding: sections.padding,
    borderRadius: sections.borderRadius,
    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : sections.border,
    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
  };

  const headingContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: open ? spacing.sm : 0
  };

  const headingStyle = {
    ...(level === 2 ? typography.h2 : typography.h3),
    margin: 0,
    flex: 1,
    color: isDarkMode ? '#e5e7eb' : colors.text
  };

  const buttonStyle = {
    ...interactive.button,
    fontSize: typography.small.fontSize,
    padding: '4px 8px',
    backgroundColor: isDarkMode ? '#242831' : colors.background,
    borderColor: isDarkMode ? '#3a4553' : colors.border,
    color: isDarkMode ? '#e2e8f0' : colors.text
  };

  const linkStyle = {
    ...interactive.link,
    ...typography.small,
    marginLeft: 'auto',
    textDecoration: 'none',
    color: isDarkMode ? '#60a5fa' : colors.link
  };

  const contentStyle = {
    marginTop: spacing.sm,
    ...sections.content,
    color: isDarkMode ? '#cbd5e1' : colors.text
  };

  return (
    <section id={section.id} data-legal-id={section.id} style={sectionStyle}>
      <div style={headingContainerStyle}>
        <HeadingTag style={headingStyle}>{section.title}</HeadingTag>
        <button
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={`${section.id}-content`}
          title={open ? translations.collapseTitle : translations.expandTitle}
          style={buttonStyle}
          onMouseEnter={(e) => {
            e.target.style.background = isDarkMode ? '#2d3748' : colors.backgroundHover;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = isDarkMode ? '#242831' : colors.background;
          }}
        >
          {open ? translations.hide : translations.show}
        </button>
        <a
          href={`#${section.id}`}
          title={translations.copyLinkTitle}
          style={linkStyle}
        >
          #{section.id}
        </a>
      </div>
      {open ? (
        <div id={`${section.id}-content`} ref={sectionRef} style={contentStyle}>
          {Array.isArray(section.content) ? section.content.map((para, i) => (
            <p key={i} style={{ ...typography.body, marginTop: spacing.xs, marginBottom: spacing.sm, color: isDarkMode ? '#cbd5e1' : colors.text }}>
              {para}
            </p>
          )) : renderMarkdown(section.content, section.id, isDarkMode)}
          {section.children && section.children.length > 0
            ? section.children.map(child => (
                <LegalSection key={child.id} section={child} level={Math.min(level + 1, 6)} />
              ))
            : null}
        </div>
      ) : null}
    </section>
  );
};

export default LegalSection;
